import { useState, useCallback, useRef } from 'react';
import { Message, GetUserUUID, CustomApiConfig } from '@components'
import { usePyrenzAlert } from '@renderer/provider';
import {
  Utils,
  NotificationManager,
  saveMessageCurrentIndex,
} from '@client/utility';
import { useChatStore } from '@client/store';
import { useGenerateAPI } from '@client/components/hooks/chatpage-hooks/generateMessageAPI';

interface UserDataRow {
  inference_settings: any;
  use_custom_api: boolean;
}

interface PrivateModelRow {
  model_url: string;
  model_api_key?: string;
}

interface ModelCheckResult {
  use_custom_api: boolean;
  model_url?: string;
  encrypted_api_key?: string;
  checkedAt: number;
}

const modelCheckCache = new Map<string, ModelCheckResult>();
const CACHE_TTL = 5 * 60 * 1000;

export const useGenerateMessage = () => {
  const showAlert = usePyrenzAlert();
  const [isStreaming, setIsStreaming] = useState(false);
  const { setMessages, user, character, messages } = useChatStore.getState();
  const { generate, stop } = useGenerateAPI();
  
  const pendingChecks = useRef<Map<string, Promise<ModelCheckResult>>>(new Map());

  const getCachedModelCheck = useCallback(async (char_uuid: string): Promise<ModelCheckResult> => {
    const now = Date.now();
    const cached = modelCheckCache.get(char_uuid);
    
    if (cached && (now - cached.checkedAt) < CACHE_TTL) {
      return cached;
    }

    const pending = pendingChecks.current.get(char_uuid);
    if (pending) {
      return pending;
    }

    const checkPromise = (async (): Promise<ModelCheckResult> => {
      try {
        const user_uuid = await GetUserUUID();
        
        const { data: userData } = await Utils.db.select<UserDataRow>({
          tables: 'user_data',
          columns: 'use_custom_api',
          match: { user_uuid },
        });

        const use_custom_api = userData?.[0]?.use_custom_api ?? false;

        if (use_custom_api) {
          const { data: privateModelData } = await Utils.db.select<PrivateModelRow>({
            tables: 'private_models',
            columns: 'model_url, model_api_key',
            match: { user_uuid },
          });

          if (privateModelData && privateModelData.length > 0) {
            const pm = privateModelData[0];
            const result: ModelCheckResult = {
              use_custom_api: true,
              model_url: pm.model_url,
              encrypted_api_key: pm.model_api_key,
              checkedAt: now,
            };
            modelCheckCache.set(char_uuid, result);
            return result;
          }
        }

        const result: ModelCheckResult = {
          use_custom_api: false,
          checkedAt: now,
        };
        modelCheckCache.set(char_uuid, result);
        return result;

      } finally {
        pendingChecks.current.delete(char_uuid);
      }
    })();

    pendingChecks.current.set(char_uuid, checkPromise);
    return checkPromise;
  }, []);

  const generateMessage = useCallback(
    async (
      text: string,
      chat_uuid: string,
      current: number,
      generationType: 'Generate' | 'Regenerate' = 'Generate',
      messageRefId?: number,
      isFirstMessage?: boolean,
      message_type: 'char' | 'user' = 'char'
    ) => {
      if (!user || !character || !chat_uuid) return;

      setIsStreaming(true);

      try {
        const user_uuid = await GetUserUUID();
        
        const modelCheck = await getCachedModelCheck(character.char_uuid);

        const { data: userData } = await Utils.db.select<UserDataRow>({
          tables: 'user_data',
          columns: 'inference_settings',
          match: { user_uuid },
        });

        if (!userData?.[0]) throw new Error('Missing inference settings');

        const customApiConfig: CustomApiConfig | undefined = modelCheck.use_custom_api && modelCheck.model_url
          ? {
              use_custom_api: true as const,
              model_url: modelCheck.model_url,
              encrypted_api_key: modelCheck.encrypted_api_key,
            }
          : undefined;

        let currentRefId = messageRefId;

        if (generationType === 'Generate') {
          const inserted = await Utils.db.insert({
            tables: 'chat_messages',
            data: {
              chat_uuid,
              user_uuid: user_uuid,
              user_message: text,
              char_message: null,
            },
          });

          const dbMessage = inserted[0] as any;
          currentRefId = Number(dbMessage.id);

          setMessages((prev) => [
            ...prev,
            {
              id: currentRefId,
              ref_id: currentRefId,
              type: 'user',
              text,
              name: user.username,
              isGenerate: false,
              chat_uuid,
            } as Message,
            {
              id: currentRefId,
              ref_id: currentRefId,
              type: 'char',
              text: '',
              isGenerate: true,
              chat_uuid,
              char_uuid: character.char_uuid,
              alternative_messages: [],
              current: 1,
              stop_stream: false,
            } as Message,
          ]);
        } else {
          if (!currentRefId)
            throw new Error('message reference id required for regenerate');

          setMessages((prev) =>
            prev.map((msg) =>
              msg.type === message_type && msg.ref_id === currentRefId
                ? {
                    ...msg,
                    text: '',
                    isGenerate: true,
                    alternative_messages: [
                      ...(msg.alternative_messages || []),
                      msg.text && msg.text.trim() !== '' ? msg.text : '',
                    ].filter((alt) => alt !== ''),
                    current: (msg.current ?? 0) + 1,
                    stop_stream: false,
                  }
                : msg
            )
          );

          const updatedMessage = useChatStore
            .getState()
            .messages.find((m) => m.ref_id === currentRefId);
          if (updatedMessage) {
            saveMessageCurrentIndex(
              currentRefId,
              updatedMessage.current ?? 1
            );
          }
        }

        const hasFirstMessage = messages.some(m => m.is_first);
        const isActuallyFirstMessage = !hasFirstMessage || isFirstMessage;

        await generate(
          {
            user_uuid,
            chat_uuid,
            char_uuid: character.char_uuid,
            messageRefId: currentRefId!,
            message_type,
            generationType,
            isFirstMessage: isActuallyFirstMessage,
            ...(customApiConfig && { custom_api_config: customApiConfig }),
          },
          {
            onChunk: (_chunk, fullText) =>
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.type === message_type && msg.ref_id === currentRefId
                    ? { ...msg, text: fullText }
                    : msg
                )
              ),
            onDone: async (finalText) => {
              setIsStreaming(false);
              
              if (message_type === 'char' && currentRefId) {
                try {
                  await Utils.db.update({
                    tables: 'chat_messages',
                    values: { char_message: finalText },
                    match: { id: currentRefId },
                  });
                } catch (updateErr) {
                  console.error('Failed to update char_message:', updateErr);
                }
              }

              setMessages((prev) =>
                prev.map((msg) =>
                  msg.type === message_type && msg.ref_id === currentRefId
                    ? {
                        ...msg,
                        text: finalText,
                        isGenerate: false,
                        stop_stream: false,
                      }
                    : msg
                )
              );
              
              if (message_type === 'char' && finalText.trim()) {
                await NotificationManager.fire({
                  title: '{{char}} replied',
                  body: finalText.slice(0, 100),
                  icon: character.profile_image,
                  charName: character.name,
                  userName: user.username,
                });
              }
            },
            onError: () => setIsStreaming(false),
            onStop: () => setIsStreaming(false),
          }
        );
      } catch (err) {
        setIsStreaming(false);
        showAlert(
          err instanceof Error ? err.message : 'Unknown error',
          'Alert'
        );
      }
    },
    [user, character, messages, setMessages, showAlert, generate, getCachedModelCheck]
  );

  const stopStream = useCallback(() => stop(), [stop]);

  const clearModelCache = useCallback((char_uuid?: string) => {
    if (char_uuid) {
      modelCheckCache.delete(char_uuid);
    } else {
      modelCheckCache.clear();
    }
  }, []);

  return { generateMessage, stopStream, isStreaming, clearModelCache };
};